import React, { useRef, useEffect } from 'react'
import { createUseStyles } from 'react-jss'
import cn from 'classnames'
import { useSelector } from 'react-redux'
import {
  getMenuItems,
  getSubMenuItems,
  getAcknowledgement,
  getFooterPartnersLabel,
  getFooterPartners,
  getPageBackgroundColor
} from '../redux/slices/content'
import Link, { LinkWithCursorHoverEffect } from './Link'
import { resolveLink } from '../helpers/resolveLink'
import { span } from '../style/span'
import theme from '../style/theme'
import { vw } from '../style/vw'
import SplitWordsAnimation from './SplitWordsAnimation'
import gsap from 'gsap'
import { useHoverCursor } from './Cursor'
import Color from 'color'
import { oneLine } from 'common-tags'

function MenuItem ({ item, animate }) {
  const classes = useStyles()
  const ref = useRef()
  useHoverCursor(ref)
  return (
    <li>
      <Link className={classes.mainMenuLink} link={resolveLink(item.link)} ref={ref}>
        <SplitWordsAnimation>
          <span>
            {item.link.text}
          </span>
        </SplitWordsAnimation>
      </Link>
    </li>
  )
}

function Menu ({ showPartners, reverse, animate }) {
  const classes = useStyles()
  const menu = useSelector(getMenuItems)
  const submenu = useSelector(getSubMenuItems)
  const acknowledgement = useSelector(getAcknowledgement)
  const partnerLabel = useSelector(getFooterPartnersLabel)
  const partners = useSelector(getFooterPartners)
  const submenuNavRef = useRef()
  const foregroundColor = Color(theme.getForegroundColor(useSelector(getPageBackgroundColor)))

  // Debug logging
  // console.log('Menu component rendered with showPartners:', showPartners)

  useEffect(() => {
    if (animate && submenuNavRef.current) {
      gsap.to(submenuNavRef.current.children, { opacity: 1, y: 0, duration: 1, stagger: 0.1 })
    }
  }, [animate])

  useEffect(() => {
    // Load Klaviyo script only when showPartners is true (footer)
    if (showPartners) {
      // console.log('Loading Klaviyo script...')
      // Check if script is already loaded
      if (!document.querySelector('script[src*="klaviyo.js"]')) {
        const script = document.createElement('script')
        script.async = true
        script.type = 'text/javascript'
        // Updated to use the correct company ID - you may need to replace this with your actual company ID
        script.src = 'https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=SqM297'
        script.onload = () => {
          // console.log('Klaviyo script loaded successfully')
          // Initialize Klaviyo forms after script loads
          setTimeout(() => {
            if (window.klaviyo && window.klaviyo.forms) {
              window.klaviyo.forms.render()
            }
          }, 100)
        }
        script.onerror = () => {
          console.error('Failed to load Klaviyo script')
        }
        document.head.appendChild(script)
      } else {
        console.log('Klaviyo script already exists')
        // Script already exists, just render forms
        setTimeout(() => {
          if (window.klaviyo && window.klaviyo.forms) {
            window.klaviyo.forms.render()
          }
        }, 100)
      }
    } else {
      console.log('showPartners is false, not loading Klaviyo')
    }
  }, [showPartners])

  return (
    <div className={cn(classes.container, { reverse })}>
      <nav className={classes.mainNav}>
        <ul className={classes.mainMenuLinks}>
          {menu && menu.map((item, i) => (
            <MenuItem key={i} item={item} />
          ))}
        </ul>
      </nav>
      <nav className={classes.submenuNav} ref={submenuNavRef}>
        {showPartners && (
          <div className={classes.klaviyoWidget}>
            <div className='klaviyo-form-U32pQb'> </div>
          </div>
        )}
        <ul className={classes.submenuLinks}>
          {submenu && submenu.map((item, i) => <li key={i}><LinkWithCursorHoverEffect className={classes.subMenuLink} link={resolveLink(item.link[0])} /></li>)}
        </ul>
        {reverse && (
          <p className={classes.acknowledgement}>
            {acknowledgement}
          </p>
        )}
        {showPartners && (
          <div className={classes.partners}>
            <span className={classes.label}>{partnerLabel}</span>
            {partners && partners.map((partner, i) => {
              const image = partner.image
              return (
                <LinkWithCursorHoverEffect key={i} link={resolveLink(partner.link[0])}>
                  <svg viewBox={`0 0 ${partner.image.aspect * 100} 100`} className={classes.partnerImage}>
                    <defs>
                      <filter id='colorChange'>
                        <feColorMatrix
                          type='matrix'
                          values={oneLine(`${foregroundColor.red() / 255}     0     0     0     0
                                           0     ${foregroundColor.green() / 255}     0     0     0
                                           0     0     ${foregroundColor.blue() / 255}     0     0
                                           0     0     0     1     0`)}
                        />
                      </filter>
                    </defs>
                    <image href={partner.image.sizes[0].url} width='100%' x='0' y='0' filter='url(#colorChange)' />
                  </svg>
                </LinkWithCursorHoverEffect>
              )
            })}
          </div>
        )}
      </nav>
    </div>
  )
}

const useStyles = createUseStyles({
  container: {
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'stretch'
    },
    '&.reverse': {
      flexDirection: 'row-reverse',
      alignItems: 'center',
      gap: span(2),

      '& $mainNav, & $submenuNav': {
        width: '100%'
      },

      '& $submenuNav': {
        marginTop: vw(100),

        [theme.breakpoints.up('md')]: {
          marginTop: 0
        }
      },

      '& $mainMenuLinks': {
        marginTop: 0
      },

      '& $mainMenuLink, & $subMenuLink': {
        color: theme.colors.white
      }

    }
  },
  mainNav: {
    // [theme.breakpoints.up('md')]: {
    //   paddingRight: span(1, 'md')
    // }
  },
  mainMenuLinks: {
    listStyle: 'none',
    // margin: [0, 0, vw(50), 0],
    padding: 0,
    lineHeight: 1,
    // [theme.breakpoints.up('md')]: {
    //   margin: [0, 0, 0, span(1, 'md')]
    // },
    '& > li:not(:last-child)': {
      marginBottom: vw(20),
      [theme.breakpoints.up('md')]: {
        marginBottom: vw(50, 'desktop')
      }
    },
    '& > li': {
      position: 'relative'
    }
  },
  mainMenuLink: {
    display: 'inline-block',
    fontFamily: theme.fonts.headings,
    fontWeight: theme.fonts.headingsFontWeight,
    fontSize: vw(36),
    textDecoration: 'none',
    color: theme.colors.primary,
    [theme.breakpoints.up('md')]: {
      fontSize: vw(54, 'desktop')
    }
  },
  submenuNav: {
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      // width: span(7, 'md'),
      paddingLeft: span(1, 'md')
    },
    '& > *': {
      opacity: 0,
      transform: 'translate(0, 100px)'
    }
  },
  klaviyoWidget: {
    marginBottom: vw(40),
    width: '100%',
    [theme.breakpoints.up('md')]: {
      marginBottom: vw(40, 'desktop')
    },
    // Klaviyo form styling
    '& .klaviyo-form-U32pQb': {
      width: '100%'
    },
    // Style the h3 in rich text (combined styles)
    '& h3': {
      fontSize: '24px !important',
      lineHeight: '1.2 !important',
      fontFamily: 'MorionRegular, Arial, "Helvetica Neue", Helvetica, sans-serif !important',
      fontWeight: '400 !important',
      color: 'rgb(56, 82, 79) !important',
      textAlign: 'left !important',
      margin: '0 0 25px 0 !important'
    },
    '& input[type="email"]': {
      boxSizing: 'border-box !important',
      borderRadius: '0px !important',
      padding: '0 0 0 16px !important',
      height: '36px !important',
      textAlign: 'left !important',
      color: '#38524F !important',
      fontFamily: 'Poppins-Klaviyo-Hosted, Arial, "Helvetica Neue", Helvetica, sans-serif !important',
      fontSize: '14px !important',
      fontWeight: '400 !important',
      letterSpacing: '0px !important',
      backgroundColor: 'rgba(255, 255, 255, 0) !important',
      borderBottom: '1px solid rgba(56, 82, 79, 0.337) !important',
      border: 'none !important',
      outline: 'none !important',
      width: '100% !important',
      '&:focus': {
        borderBottom: '2px solid rgba(56, 82, 79, 0.7) !important',
        outline: 'none !important'
      },
      '&::placeholder': {
        color: '#38524F !important',
        fontSize: '14px !important'
      }
    },
    // Style the form container
    '& form': {
      display: 'flex !important',
      flexDirection: 'column !important',
      gap: '10px !important'
    },
    // Style labels if any
    '& label': {
      fontSize: '14px !important',
      color: theme.colors.primary + ' !important',
      marginBottom: '5px !important'
    }
  },
  submenuLinks: {
    listStyle: 'none',
    marginTop: vw(20),
    padding: 0,
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    [theme.breakpoints.up('md')]: {
      marginTop: vw(0, 'desktop')
    },
    '& > li': {
      width: '50%',
      display: 'block',
      marginBottom: vw(40),
      [theme.breakpoints.up('md')]: {
        marginBottom: vw(90, 'desktop')
      }
    }
  },
  subMenuLink: {
    fontFamily: theme.fonts.headings,
    fontSize: vw(16),
    color: theme.colors.primary,
    textDecoration: 'none',
    fontWeight: 'bold',
    [theme.breakpoints.up('md')]: {
      fontSize: vw(24, 'desktop')
    }
  },
  acknowledgement: {
    fontSize: vw(12),
    lineHeight: 1.4,
    marginTop: vw(40),
    [theme.breakpoints.up('md')]: {
      marginTop: 0,
      fontSize: vw(14, 'desktop')
    }
  },
  partners: {
    display: 'flex',
    marginTop: vw(50),
    [theme.breakpoints.up('md')]: {
      marginTop: vw(100, 'desktop')
    }
  },
  label: {
    opacity: 0.5,
    fontSize: vw(14),
    marginRight: vw(20),
    [theme.breakpoints.up('md')]: {
      fontSize: vw(14, 'desktop'),
      marginRight: vw(20, 'desktop')
    }
  },
  partnerImage: {
    width: vw(140),
    [theme.breakpoints.up('md')]: {
      width: vw(140, 'desktop')
    }
  }
}, { name: 'Menu' })

export default Menu
